import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  EditIcon,
  InfoIcon,
  PowerIcon,
  SettingsIcon,
} from "lucide-react";
import {
  usePackageTemplateQuery,
  usePackageTemplateUsageStatsQuery,
  useTogglePackageTemplateStatus,
} from "../api/queries";

export function PackageTemplateDetailPage() {
  const nav = useNavigate({
    from: "/app/packages/templates/$id",
  });
  const { id } = useParams({ from: "/app/packages/templates/$id" });
  const { data, ...result } = usePackageTemplateQuery(id);
  const { data: usageStats } = usePackageTemplateUsageStatsQuery(id);
  const toggleStatus = useTogglePackageTemplateStatus(id);

  const template = data;

  const handleToggleStatus = async () => {
    if (!template) return;

    const newStatus = template.status === "active" ? "inactive" : "active";
    const statusLabel = newStatus === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";

    await toast.promise(toggleStatus.run(newStatus), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: `${statusLabel} template ແພັກເກດສຳເລັດ`,
      error: "ປ່ຽນສະຖານະ template ແພັກເກດລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Template ແພັກເກດ", to: "/app/packages/templates" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-bold text-3xl tracking-tight">
            {template?.name || "Loading..."}
          </h1>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/packages/templates" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນລາຍການ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ template ແພັກເກດ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!template ? null : (
            <div className="space-y-6">
              {/* Usage Statistics Card */}
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="rounded-lg border p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ລວມທັງໝົດທີ່ຂາຍ
                  </p>
                  <p className="font-bold text-2xl">
                    {usageStats?.totalSold.toLocaleString() ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ແພັກເກດທີ່ໃຊ້ງານຢູ່
                  </p>
                  <p className="font-bold text-2xl text-green-700">
                    {usageStats?.active.toLocaleString() ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ໝົດອາຍຸ
                  </p>
                  <p className="font-bold text-2xl text-red-700">
                    {usageStats?.expired.toLocaleString() ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຍົກເລີກ
                  </p>
                  <p className="font-bold text-2xl">
                    {usageStats?.canceled.toLocaleString() ?? 0}
                  </p>
                </div>
              </div>

              {/* General Information Card */}
              <div className="rounded-xl border bg-card">
                <div className="flex items-center justify-between border-b p-6">
                  <div className="flex items-center gap-2">
                    <InfoIcon className="size-5 text-primary" />
                    <h3 className="font-semibold text-lg">ຂໍ້ມູນທົ່ວໄປ</h3>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        nav({
                          to: "/app/packages/templates/$id/edit",
                          params: { id },
                        })
                      }
                    >
                      <EditIcon className="size-4" />
                      ແກ້ໄຂ Template
                    </Button>
                    <Button
                      variant={
                        template.status === "active" ? "destructive" : "default"
                      }
                      size="sm"
                      onClick={handleToggleStatus}
                      disabled={toggleStatus.isPending}
                    >
                      <PowerIcon className="size-4" />
                      {template.status === "active"
                        ? "ປິດການໃຊ້ງານ"
                        : "ເປີດການໃຊ້ງານ"}
                    </Button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຊື່ Template
                      </div>
                      <p className="font-semibold text-lg">{template.name}</p>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຈຳນວນຄັ້ງ
                      </div>
                      <p className="font-semibold text-lg">
                        {template.totalSessions} ຄັ້ງ
                      </p>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຈຳນວນວັນ
                      </div>
                      <p className="font-semibold text-lg">
                        {template.validDays} ວັນ
                      </p>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ລາຄາພື້ນຖານ
                      </div>
                      <p className="font-semibold text-lg text-primary">
                        {template.price.toLocaleString()} ₭
                      </p>
                    </div>
                    {template.maxSelectableClasses != null &&
                      template.maxSelectableClasses >= 1 && (
                        <div>
                          <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                            ຈຳນວນຄລາດສ໌ທີ່ເລືອກໄດ້
                          </div>
                          <p className="font-semibold text-lg">
                            {template.maxSelectableClasses} ຄລາດສ໌
                          </p>
                          <p className="text-muted-foreground text-xs">
                            ນັກຮຽນເລືອກຕາມຈຳນວນນີ້ເວລາຂາຍ
                          </p>
                        </div>
                      )}
                  </div>
                  {template.description && (
                    <div className="mt-6">
                      <div className="mb-2 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຄຳອະທິບາຍ
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {template.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Allowed Rules Card */}
              <div className="rounded-xl border bg-card">
                <div className="flex items-center justify-between border-b p-6">
                  <div className="flex items-center gap-2">
                    <SettingsIcon className="size-5 text-primary" />
                    <h3 className="font-semibold text-lg">ກົດລະບຽບທີ່ອະນຸຍາດ</h3>
                  </div>
                  {template.rules && template.rules.length > 1 && (
                    <Badge variant="default">MULTI-CLASS ACCESS</Badge>
                  )}
                </div>
                <div className="p-6">
                  {!template.rules || template.rules.length === 0 ? (
                    <p className="text-muted-foreground">ຍັງບໍ່ມີກົດລະບຽບ</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ປະເພດຄລາດສ໌</TableHead>
                            <TableHead>ລະດັບ</TableHead>
                            <TableHead>ວັນທີສ້າງ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {template.rules.map((rule) => (
                            <TableRow key={rule.id}>
                              <TableCell className="font-medium">
                                {rule.classTypeName}
                              </TableCell>
                              <TableCell>{rule.levelName}</TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {formatDate(rule.createdAt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
